import { useImage } from "@hooks/useImage";
import { useRichText } from "@hooks/useRichText";
import { useRoutes } from "@hooks/useRoutes";
import {
  type ComponentProps,
  type FromSanityGraphQL,
  type GatsbyCustomImage,
  type ResolvedLink,
} from "@ts/components";
import {
  type CustomImage,
  type FeaturedCollectionTile,
  type SectionFeaturedCollectionTiles,
} from "@ts/sanity";
import React, { type FC } from "react";

type FeaturedCollectionTileFromQuery = FeaturedCollectionTile & {
  mobileImage: CustomImage;
};

type FeaturedCollectionTilesInputProps = ComponentProps &
  FromSanityGraphQL<SectionFeaturedCollectionTiles> & {
    sections?: FeaturedCollectionTileFromQuery[];
  };

type SectionLink = Omit<FeaturedCollectionTileFromQuery, "link"> & {
  link: ResolvedLink;
  image: GatsbyCustomImage;
  mobileImage: GatsbyCustomImage;
};

type FeaturedCollectionTilesOutputProps = Omit<
  FeaturedCollectionTilesInputProps,
  "sections"
> & {
  sectionLinks: SectionLink[];
  backgroundColor?: string;
};

export const withFeaturedCollectionTiles =
  (Component: FC<FeaturedCollectionTilesOutputProps>) =>
  ({
    name = "FeaturedCollectionTiles",
    sections,
    ...rest
  }: FeaturedCollectionTilesInputProps) => {
    const { linkResolver } = useRoutes();
    const { getFluidImage } = useImage();
    const { parseContent } = useRichText();
    const sectionLinks = sections?.map((section) => ({
      ...section,
      link: linkResolver(section.link[0]),
      content: parseContent(section.content),
      image: getFluidImage(section.image, { layout: "fullWidth" }),
      mobileImage: getFluidImage(section.mobileImage, {
        layout: "constrained",
      }),
    }));

    Component.displayName = name;

    if (!sectionLinks?.length) {
      return null;
    }

    return <Component {...rest} sectionLinks={sectionLinks} />;
  };
